import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Button, Checkbox, Form, Input, Modal, Pagination,
  Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import BreadCrumbs from '../../../common/components/Breadcrumbs';
import { Action, Page } from '../../../common/constants/pageAction';
import ability from '../../../common/ability';
import snAiReviewService, {
  FieldKey, ISnAiSuggestion,
} from '../../../services/cmdb/snAiReview/snAiReview.service';
import './snAiReview.less';

const { Text } = Typography;
const { Option } = Select;

const FIELDS: FieldKey[] = ['licensable', 'product', 'version', 'edition', 'metric', 'publisher'];

const confidenceColor = (c: string) =>
  c === 'High' ? 'success' : c === 'Medium' ? 'warning' : 'error';

const SnAiReview: React.FC = () => {
  const [data, setData] = useState<ISnAiSuggestion[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confidenceFilter, setConfidenceFilter] = useState<string | undefined>(undefined);
  const [publisherFilter, setPublisherFilter] = useState<string | undefined>(undefined);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  // Reject dialog state
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ id: number } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [feedToLearning, setFeedToLearning] = useState(true);
  const [rejectLoading, setRejectLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await snAiReviewService.getQueue(page, limit);
      setData(result.data);
      setTotal(result.count);
    } catch {
      // request interceptor handles error toast
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);

  const handleAcceptField = async (id: number, field: FieldKey) => {
    try {
      await snAiReviewService.acceptField(id, field);
      toast.success('Field accepted.');
      load();
    } catch { /* handled by request interceptor */ }
  };

  const handleAcceptAll = async (id: number) => {
    try {
      await snAiReviewService.acceptAll(id);
      toast.success('All suggestions accepted.');
      load();
    } catch { /* handled by request interceptor */ }
  };

  const openRejectModal = (id: number) => {
    setRejectTarget({ id });
    setRejectReason('');
    setFeedToLearning(true);
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setRejectLoading(true);
    try {
      await snAiReviewService.rejectField(rejectTarget.id, rejectReason || undefined, feedToLearning);
      toast.success('Suggestion rejected.');
      setRejectModalVisible(false);
      load();
    } catch { /* handled by request interceptor */
    } finally {
      setRejectLoading(false);
    }
  };

  const handleSkip = async (id: number) => {
    try {
      await snAiReviewService.skip(id);
      toast.info('Record skipped.');
      load();
    } catch { /* handled by request interceptor */ }
  };

  const handleTriggerBatch = async () => {
    setProcessing(true);
    try {
      const result = await snAiReviewService.triggerBatch();
      toast.success(`Batch complete — ${result.processed} records processed.`);
      load();
    } catch { /* handled by request interceptor */
    } finally {
      setProcessing(false);
    }
  };

  // Filter by confidence and publisher client-side (within the current page)
  const filteredData = data.filter((row) => {
    if (confidenceFilter && row.confidence !== confidenceFilter) return false;
    if (publisherFilter && row.suggested_publisher !== publisherFilter) return false;
    return true;
  });

  const publisherOptions = Array.from(new Set(data.map((r) => r.suggested_publisher).filter(Boolean)));

  const renderFieldCell = (record: ISnAiSuggestion, field: FieldKey) => {
    const current = record.software_normalization?.[field];
    const suggested = record[`suggested_${field}` as keyof ISnAiSuggestion] as string | null;

    if (!suggested) return <Text type="secondary">—</Text>;

    const differs = !current || current.toLowerCase() !== suggested.toLowerCase();

    if (!differs) {
      return <Text type="secondary">{suggested}</Text>;
    }

    return (
      <Space size={4} wrap className="sn-ai-field-cell">
        {current && <Text type="secondary">{current}</Text>}
        {current && <Text type="secondary">→</Text>}
        <Text strong style={{ color: '#1677ff' }}>{suggested}</Text>
        <Space size={2}>
          <Tooltip title="Accept">
            <Button
              size="small"
              type="text"
              className="sn-ai-accept-btn"
              icon={<CheckOutlined />}
              onClick={() => handleAcceptField(record.id, field)}
            />
          </Tooltip>
          <Tooltip title="Reject">
            <Button
              size="small"
              type="text"
              danger
              className="sn-ai-reject-btn"
              icon={<CloseOutlined />}
              onClick={() => openRejectModal(record.id)}
            />
          </Tooltip>
        </Space>
      </Space>
    );
  };

  const columns: ColumnsType<ISnAiSuggestion> = [
    {
      title: 'Software Title',
      dataIndex: ['software_normalization', 'software_title'],
      key: 'software_title',
      width: 260,
      render: (val) => <Text strong style={{ wordBreak: 'break-word' }}>{val}</Text>,
    },
    {
      title: 'Confidence',
      key: 'confidence',
      width: 110,
      render: (_, record) => (
        <Tag color={confidenceColor(record.confidence)}>{record.confidence}</Tag>
      ),
    },
    ...FIELDS.map((field) => ({
      title: field.charAt(0).toUpperCase() + field.slice(1),
      key: field,
      render: (_: any, record: ISnAiSuggestion) => renderFieldCell(record, field),
    })),
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" type="primary" onClick={() => handleAcceptAll(record.id)}>
            Accept All
          </Button>
          <Button size="small" onClick={() => handleSkip(record.id)}>
            Skip
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="sqlServer sn-ai-review-page">
      <div className="title-block">
        <h4 className="p-0">
          <BreadCrumbs pageName={Page.CmdbSnAiReview} />
        </h4>
      </div>

      <div className="main-card">
        {/* Toolbar */}
        <div className="sn-ai-toolbar">
          <Space wrap>
            <Select
              allowClear
              placeholder="Filter by Confidence"
              style={{ width: 180 }}
              value={confidenceFilter}
              onChange={(v) => setConfidenceFilter(v as string | undefined)}
            >
              <Option value="High">High</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Low">Low</Option>
            </Select>
            <Select
              allowClear
              placeholder="Filter by Publisher"
              style={{ width: 200 }}
              value={publisherFilter}
              onChange={(v) => setPublisherFilter(v as string | undefined)}
              showSearch
            >
              {publisherOptions.map((p) => (
                <Option key={p as string} value={p as string}>{p}</Option>
              ))}
            </Select>
            <Badge count={total} overflowCount={9999} color="blue">
              <Text type="secondary">Pending reviews</Text>
            </Badge>
          </Space>
          {ability.can(Action.View, Page.CmdbSnAiReview) && (
            <Button
              icon={<PlayCircleOutlined />}
              loading={processing}
              onClick={handleTriggerBatch}
            >
              Run Batch Now
            </Button>
          )}
        </div>

        <Table<ISnAiSuggestion>
          rowKey="id"
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={false}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as number[]),
            expandedRowRender: (record) => (
              <div className="sn-ai-reasoning">
                <Text type="secondary" strong>AI Reasoning: </Text>
                <Text type="secondary">{record.ai_reasoning || 'No reasoning provided.'}</Text>
              </div>
            ),
          }}
          locale={{ emptyText: 'No records pending review.' }}
        />

        <div className="sn-ai-pagination">
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            showSizeChanger={false}
            showTotal={(t) => `${t} records`}
            onChange={setPage}
          />
        </div>
      </div>

      {/* Reject dialog */}
      <Modal
        title="Reject Suggestion"
        visible={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleRejectConfirm}
        okText="Confirm Reject"
        okButtonProps={{ danger: true, loading: rejectLoading }}
        centered
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Reason (optional)">
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder="Why is this suggestion incorrect?"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={feedToLearning}
              onChange={(e) => setFeedToLearning(e.target.checked)}
            >
              Include in AI learning (helps improve future suggestions)
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SnAiReview;
